// Need to use the React-specific entry point to import createApi
import { createApi } from "@reduxjs/toolkit/query/react";
import { ListingResponse } from "../types/Listing";
import {
  ChangePasswordPayload,
  ChangePasswordResponse,
  GetNotificationPrefsResponse,
  NotificationPrefsPayload,
  NotificationPrefsResponse,
} from "../types/User";
import { appBaseQuery, baseQueryWithAuthHandling } from "../utils/utils";
import { API_METHODS } from "./constants";
import { API_ENDPOINTS } from "./endpoints";
import { handleAuthErrorCode } from "./errorCodes";

// Define a service using a base URL and expected endpoints
export const UserAPIs = createApi({
  reducerPath: "userAPI",
  // tagTypes: [],
  baseQuery: (args, api, extraOptions) =>
    baseQueryWithAuthHandling(
      appBaseQuery.withAuthJson,
      args,
      api,
      extraOptions
    ),
  endpoints: (builder) => ({
    // change password
    changePassword: builder.mutation<
      ChangePasswordResponse,
      ChangePasswordPayload
    >({
      query: (params: ChangePasswordPayload) => {
        return {
          url: API_ENDPOINTS.USERS.CHANGE_PASSWORD,
          method: API_METHODS.POST,
          body: params,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: async (data: ChangePasswordResponse) => {
        return data;
      },
    }),

    // Get Notfication Prefs
    getNotificationPrefs: builder.query<
      ListingResponse<GetNotificationPrefsResponse[]>,
      void
    >({
      query: () => {
        return {
          url: API_ENDPOINTS.USERS.NOTIFICATION_PREFS,
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: async (
        data: ListingResponse<GetNotificationPrefsResponse[]>
      ) => {
        return data;
      },
    }),
    // Post notification prefs
    postNotificationPrefs: builder.mutation<
      NotificationPrefsResponse,
      NotificationPrefsPayload
    >({
      query: (params: NotificationPrefsPayload) => {
        return {
          url: API_ENDPOINTS.USERS.NOTIFICATION_PREFS,
          method: API_METHODS.POST,
          body: params,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: async (data: NotificationPrefsResponse) => {
        return data;
      },
    }),

    // logout
    logout: builder.mutation<any, void>({
      query: () => {
        return {
          url: API_ENDPOINTS.USERS.LOGOUT,
          method: API_METHODS.POST,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: async (data: ChangePasswordResponse) => {
        return data;
      },
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useChangePasswordMutation,
  useLogoutMutation,
  useGetNotificationPrefsQuery,
  usePostNotificationPrefsMutation,
} = UserAPIs;
